<template>
    <Teleport to="body">
        <Transition name="pdp-sticky-atc">
            <div
                v-if="showStickyBar"
                class="fixed inset-x-0 bottom-0 z-30 border-t border-green-500 bg-white px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_16px_rgba(0,0,0,0.08)] lg:hidden"
                role="region"
                :aria-label="stickyBarAriaLabel">
                <div class="relative mx-auto flex w-full max-w-lg flex-col-reverse overflow-hidden">
                    <div class="relative z-10 bg-white pt-3">
                        <button
                            type="button"
                            class="btn btn-yellow flex! min-h-[48px] w-full justify-center"
                            :class="{
                                'pointer-events-none cursor-not-allowed opacity-40': isButtonDisabled,
                            }"
                            :disabled="isButtonDisabled"
                            :aria-busy="isAddingToCart"
                            :aria-disabled="isButtonDisabled"
                            :aria-expanded="showSizePicker && hasVariantSelection"
                            aria-controls="pdp-sticky-size-picker"
                            @click="handleStickyClick">
                            <NuxtImg
                                :alt="snippets['headless.icon.shoppingbag']"
                                class="mr-3 size-4"
                                src="/icons/shopping-bag_black.svg" />
                            <span>{{ snippets['headless.pdp.inCartButton'] }}</span>
                        </button>
                        <div class="mt-2 flex items-center justify-center text-green-700">
                            <TruckIcon class="mr-1 size-6" />
                            <p class="text-xs leading-tight mt-1 tracking-[0.6px]">
                                {{ $snippets['headless.cart.shippingTime'] }}
                            </p>
                        </div>
                    </div>

                    <Transition name="pdp-sticky-size-rise">
                        <div
                            v-if="showSizePicker && hasVariantSelection"
                            id="pdp-sticky-size-picker"
                            class="relative z-0 bg-white pt-3"
                            role="group"
                            :aria-label="sizePickerAriaLabel"
                            :aria-describedby="variantStockErrorMessage ? 'pdp-sticky-stock-error' : undefined">
                            <template v-for="variant in stickyVariantGroups" :key="variant.translated.name">
                                <p v-if="variant.translated.name === 'Maat'" class="mb-2 text-sm font-bold">
                                    {{ snippets['headless.pdp.chooseYourSize'] ?? 'Kies je maat' }}
                                </p>
                                <p v-else class="mb-2 text-sm font-bold">
                                    {{ variant.translated.name }}
                                </p>
                                <div class="flex flex-wrap gap-2">
                                    <button
                                        v-for="option in variant.options"
                                        :key="option.id"
                                        type="button"
                                        class="relative size-[50px] overflow-hidden bg-green-400 p-[4px] text-sm font-bold break-all tabular-nums duration-200 hover:bg-green-500"
                                        :class="{
                                            'pointer-events-none bg-green-600!': isOptionSelected(option),
                                            'pointer-events-none bg-green-200! text-green-500 opacity-40':
                                                option.stock === 0,
                                        }"
                                        :aria-pressed="isOptionSelected(option)"
                                        :aria-disabled="option.stock === 0"
                                        @click="handleSizeSelect(option)">
                                        {{ option.translated.name }}
                                    </button>
                                </div>
                            </template>
                            <p
                                v-if="variantStockErrorMessage"
                                id="pdp-sticky-stock-error"
                                role="alert"
                                class="mt-3 flex items-center text-sm leading-snug text-red-700">
                                <InformationCircleIcon class="mr-2 size-5 shrink-0 stroke-2" aria-hidden="true" />
                                {{ variantStockErrorMessage }}
                            </p>
                        </div>
                    </Transition>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
    import { unref } from 'vue';
    import type {
        ProductDetailAddToCartExposed,
        ProductDetailVariantOption,
    } from '~/composables/useProductDetailAddToCartExposed';
    import { TruckIcon, InformationCircleIcon } from '@heroicons/vue/24/outline';

    const props = defineProps<{
        addToCartExposed?: ProductDetailAddToCartExposed | null;
    }>();

    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    const primaryCtaVisible = ref(true);
    const showSizePicker = ref(false);
    const isMobileViewport = ref(false);
    let intersectionObserver: IntersectionObserver | null = null;
    let mobileMediaQuery: MediaQueryList | null = null;

    const MOBILE_MEDIA_QUERY = '(max-width: 1023px)';

    function syncMobileViewport() {
        if (!import.meta.client) {
            return;
        }
        isMobileViewport.value = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    }

    function getExposed(): ProductDetailAddToCartExposed | null {
        return props.addToCartExposed ?? null;
    }

    function resolveAnchor(): HTMLElement | null {
        const anchor = unref(getExposed()?.primaryCtaAnchor);
        return anchor ?? null;
    }

    function teardownObserver() {
        intersectionObserver?.disconnect();
        intersectionObserver = null;
    }

    function setupObserver() {
        teardownObserver();

        if (!isMobileViewport.value || unref(getExposed()?.isSoldOut)) {
            primaryCtaVisible.value = true;
            return;
        }

        const el = resolveAnchor();
        if (!el || !import.meta.client) {
            primaryCtaVisible.value = true;
            return;
        }

        intersectionObserver = new IntersectionObserver(
            ([entry]) => {
                primaryCtaVisible.value = entry?.isIntersecting ?? false;
            },
            { threshold: 0.01 },
        );
        intersectionObserver.observe(el);
    }

    watch(
        () => props.addToCartExposed,
        () => nextTick(setupObserver),
        { immediate: true },
    );

    function onMobileViewportChange() {
        syncMobileViewport();
        showSizePicker.value = false;
        nextTick(setupObserver);
    }

    onMounted(() => {
        syncMobileViewport();
        mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
        mobileMediaQuery.addEventListener('change', onMobileViewportChange);
        nextTick(setupObserver);
    });

    onBeforeUnmount(() => {
        mobileMediaQuery?.removeEventListener('change', onMobileViewportChange);
        mobileMediaQuery = null;
        teardownObserver();
    });

    const isSoldOut = computed(() => unref(getExposed()?.isSoldOut) ?? false);

    const showStickyBar = computed(
        () => import.meta.client && isMobileViewport.value && !isSoldOut.value && !primaryCtaVisible.value,
    );

    const canAddToCart = computed(() => unref(getExposed()?.canAddToCart) ?? false);
    const isAddingToCart = computed(() => unref(getExposed()?.isAddingToCart) ?? false);
    const isVariantOutOfStock = computed(() => unref(getExposed()?.isVariantOutOfStock) ?? false);
    const variantStockErrorMessage = computed(() => unref(getExposed()?.variantStockErrorMessage) ?? null);

    const stickyVariantGroups = computed(() => unref(getExposed()?.stickyVariantGroups) ?? []);

    const hasVariantSelection = computed(() => stickyVariantGroups.value.length > 0);

    const isButtonDisabled = computed(() => isSoldOut.value || isAddingToCart.value);

    const stickyBarAriaLabel = computed(
        () => snippets['headless.pdp.stickyAddToCartRegion'] ?? 'Snel toevoegen aan winkelwagen',
    );

    const sizePickerAriaLabel = computed(() => snippets['headless.pdp.chooseYourSize'] ?? 'Kies je maat');

    function isOptionSelected(option: ProductDetailVariantOption): boolean {
        return getExposed()?.isSelected(option) ?? false;
    }

    async function handleSizeSelect(option: ProductDetailVariantOption) {
        const exposed = getExposed();
        if (!exposed || option.stock === 0) {
            return;
        }
        await exposed.selectOption(option);
    }

    watch(showStickyBar, visible => {
        if (!visible) {
            showSizePicker.value = false;
        }
    });

    async function completeStickyAddToCart(): Promise<boolean> {
        const exposed = getExposed();
        if (!exposed) {
            return false;
        }
        const added = await exposed.addProductAndRefresh();
        if (added) {
            exposed.clearVariantSelection();
            showSizePicker.value = false;
        }
        return added;
    }

    watch(canAddToCart, async can => {
        if (!can || !showSizePicker.value) {
            return;
        }
        await completeStickyAddToCart();
    });

    watch(isVariantOutOfStock, outOfStock => {
        if (outOfStock && showStickyBar.value && hasVariantSelection.value) {
            showSizePicker.value = true;
        }
    });

    async function handleStickyClick() {
        const exposed = getExposed();
        if (!exposed) {
            return;
        }
        if (canAddToCart.value) {
            if (isVariantOutOfStock.value) {
                showSizePicker.value = true;
                return;
            }
            await completeStickyAddToCart();
            return;
        }
        if (!isSoldOut.value && hasVariantSelection.value) {
            showSizePicker.value = true;
        }
    }
</script>

<style scoped>
    .pdp-sticky-atc-enter-active,
    .pdp-sticky-atc-leave-active {
        transition:
            transform 0.25s ease,
            opacity 0.25s ease;
    }

    .pdp-sticky-atc-enter-from,
    .pdp-sticky-atc-leave-to {
        transform: translateY(100%);
        opacity: 0;
    }

    .pdp-sticky-size-rise-enter-active,
    .pdp-sticky-size-rise-leave-active {
        transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
    }

    .pdp-sticky-size-rise-enter-from,
    .pdp-sticky-size-rise-leave-to {
        transform: translateY(100%);
    }
</style>
